'use strict';

/**
 * DmtModule — Domestic Money Transfer operations.
 *
 * Every call goes through fintech-gateway's wildcard proxy at
 * POST/GET /process/<upstreamPath>. DMT spans two paymentSystem route
 * groups (see fintech-gateway/docs/qr-aeps-dmt-operations.md):
 *   - customer (remitter) onboarding, mounted at /dmt
 *   - remittance transactions,        mounted at /dmt/txn/v1
 *
 * Field names below are corrected against webpayment's stores/dmt.ts +
 * composables/apis/useDmtCustomerApi.ts/useDmtTxnApi.ts, which document
 * several live-confirmed corrections to the gateway ops doc (e.g.
 * accountVerification's real fields, addBeneficiary's response shape) —
 * treat webpayment's implementation as the source of truth over the doc
 * where they disagree.
 */
class DmtModule {
  constructor(client) {
    this._c = client;
  }

  _post(path, body = {}) { return this._c.post(path, body); }
  _get(path, body = {})  { return this._c.get(path, body); }

  // ── Customer (remitter) onboarding — /dmt/customer/* ──────────────

  /** @param {{ mobile_number: string }} body */
  getCustomerDetail(body)         { return this._post('dmt/customer/GetDetail', body); }

  /** @param {{ name: string, mobile_no: string, email_id: string, city: string, state: string }} body */
  onboardCustomer(body)           { return this._post('dmt/customer/onboarding', body); }

  /** @param {{ referenceid: string }} body */
  requestCustomerOtp(body)        { return this._post('dmt/customer/request_otp', body); }

  /** @param {{ otp: string, otpreqid: string, referenceid: string }} body */
  validateCustomerOtp(body)       { return this._post('dmt/customer/validate_otp', body); }

  /** @param {{ aadhaarno: string, referenceid: string }} body */
  verifyCustomerUid(body)         { return this._post('dmt/customer/verify_UID', body); }

  /** @param {{ PidData: string, aadhaarno: string, referenceid: string }} body */
  updateCustomerBiodetail(body)   { return this._post('dmt/customer/updatebiodetail', body); }

  /** Submit the completed customer onboarding application. No fields. */
  submitCustomer()                { return this._post('dmt/customer/submit'); }

  /** Onboarding status of the remitter identity resolved from credentials. */
  customerStatusCheck()           { return this._get('dmt/customer/statusCheck'); }

  // ── Remittance transactions — /dmt/txn/v1/* ───────────────────────

  /** Login/refresh the aggregator's NSDL DMT session. No fields. */
  login()                         { return this._post('dmt/txn/v1/login'); }

  /** @param {{ sender_mobilenumber: string }} body */
  registerRemitter(body)          { return this._post('dmt/txn/v1/remitter/register', body); }

  /** @param {{ sender_mobilenumber?: string, senderId?: string }} body - one of the two is required */
  getRemitterDetails(body)        { return this._post('dmt/txn/v1/remitter/details', body); }

  /** @param {{ sender_mobilenumber: string, receiver_account_no: string, receiverIfscCode: string,
   *            receivername: string, receivermobilenumber: string, receiveremailid?: string }} body */
  accountVerification(body)       { return this._post('dmt/txn/v1/beneficiary/accountVerification', body); }

  /** @param {{ senderId: string, receiverIfscCode: string, receiver_account_no: string }} body */
  getBeneficiary(body)            { return this._post('dmt/txn/v1/beneficiary/get', body); }

  /** @param {{ senderId: string, receivername: string, receivermobilenumber: string,
   *            receiveremailid: string, receiverIfscCode: string, receiver_account_no: string,
   *            address?: string, city?: string, state?: string, branch_contact?: string }} body */
  addBeneficiary(body)            { return this._post('dmt/txn/v1/beneficiary/add', body); }

  /** @param {{ senderId: string, benefId: string }} body */
  deleteBeneficiary(body)         { return this._post('dmt/txn/v1/beneficiary/delete', body); }

  /** @param {{ senderId: string, amount: number, receiver_account_no: string, receiverIfscCode: string }} body */
  transactionOtp(body)            { return this._post('dmt/txn/v1/transaction/otp', body); }

  /** Generic transaction submit — same shape as transactionInstant. */
  transaction(body)                { return this._post('dmt/txn/v1/transaction', body); }

  /**
   * Instant fund transfer via IMPS/NEFT.
   * @param {{ senderId: string, BeneficiaryId: string, amount: number, refId: string,
   *            remarks?: string, otp: string, receiver_account_no: string, receiverIfscCode: string,
   *            account_holder_name: string, receiverMobilenumber?: string }} body
   */
  transactionInstant(body)        { return this._post('dmt/txn/v1/transaction/instant', body); }

  /** @param {{ senderId: string, amount: number }} body */
  transactionCharges(body)        { return this._post('dmt/txn/v1/transaction/charges', body); }

  /** Re-query a pending/ambiguous transaction. Shape passed through as-is. */
  transactionRequery(body)        { return this._post('dmt/txn/v1/transaction/requery', body); }

  /** @param {{ transactionId: string }} body */
  refundOtp(body)                 { return this._post('dmt/txn/v1/transaction/refundOTP', body); }

  /** Refund shape passed through as-is. */
  refund(body)                    { return this._post('dmt/txn/v1/refund', body); }

  // ── BC agent onboarding — /onboarding/dmt/bc/agent/* ──────────────
  // Registers *this merchant itself* as a DMT Business Correspondent agent
  // with NSDL (distinct from the customer/remitter onboarding above, which
  // onboards the merchant's own customers). checkRole is "aggregator"/
  // "vendor" only (dmt.bc.route.js) — unlike every other DMT call in this
  // module, a plain merchant clientId cannot call these two. Mirrors
  // webpayment's composables/apis/Usedmtbcapi.ts.

  /** Creates the merchant's DMT ServiceKyc record (idempotent — calling it
   *  again for an already-initialized merchant still returns success). No
   *  fields beyond merchantId (auto-injected when the client is configured
   *  with one — see GatewayClient's merchantId doc). */
  initializeAgent(body = {}) { return this._post('onboarding/dmt/bc/agent/initialize', body); }

  /** @param {{ name: string, lastname: string, pancard: string,
   *            dmt?: 0|1, aeps?: 0|1, cardpin?: 0|1, accountopen?: 0|1 }} body */
  registerAgent(body)        { return this._post('onboarding/dmt/bc/agent/registor', body); }
}

module.exports = { DmtModule };
